import { useLangFile } from '../../context/LanguageContext';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Styles from './PrescriptionArchive.module.less';
import { Card, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import moment from 'moment-timezone';
import PrescriptionArchiveSelector, {
  PrescriptionArchiveSelectorItem,
} from './PrescriptionArchiveSelector';
import PrescriptionArchiveTable from './PrescriptionArchiveTable';
import { voidFunc } from '../../constants/PropTypeUtils';
import {
  MetaTypes,
  parseLayerName,
  parseMetaType,
} from 'js/components/Prescription/PrescriptionJob';
import { useSeasonContext } from '../../context/SeasonContext';
import { getComparator, stableSort, TableColumns } from './PrescriptionArchiveUtils';
import { groupBy } from '../../algorithms/grouping';
import { getFieldName } from '../../helpers/StateInterpreters';
import PrescriptionFieldCardItem from './PrescriptionFieldCardItem';
import PropTypes from 'prop-types';
import SeasonSelector from '../Seasons/SeasonSelector';
import { isSatelliteLayer } from '../Prescription/PrescriptionJob';
import { fuzzyFilter } from 'js/algorithms/filtering';
import DateRangeInput from 'js/components/FormControls/DateRangeInput';
import {
  createArchiveCsv,
  createArchiveCsvFileName,
  getArchiveExportJobs,
  getArchiveJobField,
} from './PrescriptionArchiveCsv';
import DescriptionIcon from '@mui/icons-material/Description';
import Typography from '@mui/material/Typography';
import Field from '../../model/Field';
import SearchBar from '../SearchBar/SearchBar';

const PrescriptionArchive = (props: PrescriptionArchive.propTypes) => {
  const LangFile = useLangFile();
  const { selectedSeason } = useSeasonContext();

  const seasonStartDate = useRef(moment().startOf('year'));
  const seasonEndDate = useRef(moment().startOf('year'));
  const validMetaTypes = useRef([]);
  const validCropTypes = useRef([]);
  const validDataSources = useRef([]);

  const [metaTypes, setMetaTypes] = useState(validMetaTypes.current);
  const [cropTypes, setCropTypes] = useState(validCropTypes.current);
  const [dataSources, setDataSources] = useState(validDataSources.current);
  const [showOutsideMaps, setShowOutsideMaps] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [excludedFields, setExcludedFields] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState(TableColumns.createdDate);
  const [cards, setCards] = useState([]);
  const [datePickerFromDate, setDatePickerFromDate] = useState();
  const [datePickerToDate, setDatePickerToDate] = useState();
  const [datePickerEnabled, setDatePickerEnabled] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [filteredAndSortedMaps, setFilteredAndSortedMaps] = useState([]);

  const handleDateRangeChange = (startDate, endDate) => {
    setDatePickerFromDate(startDate);
    setDatePickerToDate(endDate);
  };

  const setDefaultFilters = useCallback(() => {
    if (props.maps) {
      const maps = props.maps.filter((m) => m.seasonId == null || m.seasonId === selectedSeason.id);

      validMetaTypes.current = maps
        .map((item) => item.metaType)
        .filter((item, idx, arr) => arr.indexOf(item) === idx);
      validCropTypes.current = maps
        .map((item) => {
          const field = props.fields.get(item.fieldId);
          return field && field.crop;
        })
        .filter(Boolean)
        .filter((item, idx, arr) => arr.indexOf(item) === idx);
      validDataSources.current = maps
        .map((item) => item.layer)
        .filter((item, idx, arr) => arr.indexOf(item) === idx);

      setMetaTypes(validMetaTypes.current);
      setCropTypes(validCropTypes.current);
      setDataSources(validDataSources.current);
      handleDateRangeChange();
      setDatePickerEnabled(false);
      setShowOutsideMaps(true);
    }
  }, [props.maps, props.fields, selectedSeason]);

  const onShowMapsOutsideChange = useCallback((event, value) => {
    setShowOutsideMaps(value);
  }, []);

  const onJobTypesChange = useCallback((metaTypes: [PrescriptionArchiveSelectorItem]) => {
    setMetaTypes(metaTypes.map((metaType) => metaType.item));
  }, []);

  const onCropTypesChange = useCallback((cropTypes: [PrescriptionArchiveSelectorItem]) => {
    setCropTypes(cropTypes.map((cropType) => cropType.item));
  }, []);

  const onDataSourcesChange = useCallback((surveyLayers: [PrescriptionArchiveSelectorItem]) => {
    setDataSources(surveyLayers.map((surveyLayer) => surveyLayer.item));
  }, []);

  const onSearchChange = useCallback((text) => {
    setSearchText(text);
  }, []);

  const onSearchCancel = useCallback(() => {
    setSearchText('');
  }, []);

  const onOrderByChange = useCallback((orderBy: string) => {
    setOrderBy(orderBy);
  }, []);

  const onOrderChange = useCallback((order: string) => {
    setOrder(order);
  }, []);

  const ondatePickerEnabledChange = useCallback((event, value) => {
    setDatePickerEnabled(value);
  }, []);

  const onJobCheckboxToggled = (value, jobs) => {
    if (value) {
      const newJobs = jobs.filter(
        (job) => !selectedJobs.some((entry) => entry.jobId === job.jobId)
      );
      setSelectedJobs([...selectedJobs, ...newJobs]);
    } else {
      const jobIdsToRemove = new Set(jobs.map((job) => job.jobId));
      const tempSelectedJobs = selectedJobs.filter((entry) => !jobIdsToRemove.has(entry.jobId));
      setSelectedJobs(tempSelectedJobs);
    }
  };

  const sort = (array) => {
    if (orderBy === TableColumns.crop) {
      return array;
    } else {
      const comparator = getComparator(order, orderBy);
      return stableSort(array, comparator);
    }
  };

  const sortCards = (array) => {
    if (orderBy === TableColumns.crop) {
      return array.sort((lhs, rhs) => {
        const lhsCrop = lhs.field.crop;
        const rhsCrop = rhs.field.crop;
        if (lhsCrop < rhsCrop) {
          return order === 'desc' ? -1 : 1;
        }
        if (lhsCrop > rhsCrop) {
          return order === 'desc' ? 1 : -1;
        }
        return 0;
      });
    } else {
      return array.sort((lhs, rhs) => {
        const lhsFieldName = getFieldName(lhs.field, LangFile);
        const rhsFieldName = getFieldName(rhs.field, LangFile);
        if (lhsFieldName < rhsFieldName) {
          return -1;
        } else {
          return lhsFieldName > rhsFieldName ? 1 : 0;
        }
      });
    }
  };

  const createDownloadLink = (content, fileName) => {
    const blob = new Blob(['\uFEFF', content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (date) => date.toLocaleDateString() + ' ' + date.toLocaleTimeString();

  const getLayerName = (layer) =>
    isSatelliteLayer(layer)
      ? LangFile.PrescriptionJob.satelliteLayers.vitality
      : LangFile.SurveyLayers[layer]?.name;

  const getSeasonName = (seasonId) =>
    seasonId === selectedSeason.id ? selectedSeason.name : LangFile.PrescriptionTableHead.noSeason;

  const generateCSVContent = (jobs) => {
    const headers = [
      LangFile.PrescriptionTableHead.field,
      LangFile.PrescriptionArchive.headers.jobName,
      LangFile.PrescriptionArchive.headers.createdDate,
      LangFile.PrescriptionArchive.headers.layer,
      LangFile.PrescriptionArchive.headers.metaType,
      LangFile.PrescriptionArchive.headers.totalPrescription,
      LangFile.PrescriptionArchive.headers.season,
    ];

    return createArchiveCsv(jobs, headers, (job) => [
      getFieldName(getArchiveJobField(cards, job), LangFile),
      job.jobName,
      formatDate(job.createdDate),
      getLayerName(job.layer),
      LangFile.PrescriptionJob.metaTypes[job.metaType.toLowerCase()],
      job.totalPrescription.toFixed(2),
      getSeasonName(job.seasonId),
    ]);
  };

  const generateCSVFromJobs = (jobs) => () => {
    if (!jobs || jobs.length === 0) return;

    const csvContent = generateCSVContent(jobs);
    const field = getArchiveJobField(cards, jobs[0]);
    const fileName = `${LangFile.PrescriptionTableHead.field}_${getFieldName(field, LangFile)}_${jobs.length > 1 ? 'jobs' : jobs[0].jobName}.csv`;

    createDownloadLink(csvContent, fileName);
  };

  const generateCSVFromCards = (cards) => () => {
    const jobs = getArchiveExportJobs(cards, selectedJobs);
    if (jobs.length === 0) return;

    const csvContent = generateCSVContent(jobs);
    const fileName = createArchiveCsvFileName(
      selectedSeason.name,
      LangFile.PrescriptionArchive.headers.season,
      moment().format('YYYY-MM-DD')
    );
    createDownloadLink(csvContent, fileName);
  };

  useEffect(() => {
    if (selectedSeason) {
      seasonStartDate.current = moment(selectedSeason.startDate);
      seasonEndDate.current = moment(selectedSeason.endDate);
    }
  }, [selectedSeason]);

  useEffect(() => {
    setDefaultFilters();
  }, [setDefaultFilters]);

  useEffect(() => {
    if (datePickerEnabled) {
      handleDateRangeChange(seasonStartDate.current, seasonEndDate.current);
    } else {
      handleDateRangeChange();
    }
  }, [datePickerEnabled]);

  useEffect(() => {
    const filterMaps = () => {
      if (order && orderBy && props.maps) {
        let result = props.maps || [];
        const cards = [];

        // Start by filtering on missing seasonId and matching seasonId
        result = result.filter(
          (map) => map.seasonId == null || map.seasonId === -1 || map.seasonId === selectedSeason.id
        );
        let numberOfFields = Object.keys(groupBy(result, 'fieldId')).length;

        // Fuzzy Filtering by search text
        if (searchText.length > 0) {
          result = result.filter((map) => {
            const field = props.fields.get(Number(map.fieldId));
            return (
              fuzzyFilter(searchText, `${map.fieldId}`) || // Field ID
              fuzzyFilter(searchText, `${getFieldName(field, LangFile)}`) || // Field Name
              fuzzyFilter(searchText, `${map.jobName}`) || // Job Name
              fuzzyFilter(searchText, `${parseLayerName(map.layer, LangFile)}`) || // Layer Name
              fuzzyFilter(searchText, `${LangFile.CropType[field?.crop ? field.crop : 'NONE']}`) || // Crop Name
              fuzzyFilter(searchText, `${moment(map.createdDate).format('LL')}`) || // Created Date
              (field?.sowing && fuzzyFilter(searchText, `${moment(field.sowing).format('LL')}`)) || // Sowing Date
              fuzzyFilter(searchText, `${parseMetaType(map.metaType, LangFile)}`) // Job Type
            );
          });
        }

        // Filtering by metaType
        if (metaTypes.length > 0) {
          result = result.filter((map) => {
            if (metaTypes.includes(MetaTypes.SEEDING)) {
              return metaTypes.indexOf(map.metaType) !== -1;
            }

            return metaTypes.indexOf(map.metaType) !== -1;
          });
        }

        // Filtering by cropType
        if (cropTypes.length > 0) {
          result = result.filter((map) => {
            const field = props.fields.get(map.fieldId);
            return field ? cropTypes.indexOf(field.crop) !== -1 : true;
          });
        }

        // Filtering by dataSource
        if (dataSources.length > 0) {
          result = result.filter((map) => dataSources.indexOf(map.layer) !== -1);
        }

        // Filter out maps created outside the date range
        if (datePickerFromDate && datePickerToDate) {
          result = result.filter((map) => {
            const createdDate = moment(map.createdDate).startOf('day');
            return createdDate.isBetween(
              datePickerFromDate.startOf('day'),
              datePickerToDate.startOf('day'),
              null,
              '[]'
            );
          });
        }

        // Handle maps outside the season
        const mapsWithUnknownFields = [];
        const outsideMapsWithUnknownFields = [];
        if (showOutsideMaps) {
          let outsideSeason = result.filter((map) => map.seasonId == null || map.seasonId === -1);
          outsideSeason = sort(outsideSeason);
          const outSideSeasonMaps = groupBy(outsideSeason, 'fieldId');
          for (const key of Object.keys(outSideSeasonMaps)) {
            const field = props.fields.get(Number(key));
            if (field) {
              cards.push(new PrescriptionFieldCardItem(field, [], outSideSeasonMaps[key]));
            } else {
              outsideMapsWithUnknownFields.push(...outSideSeasonMaps[key]);
            }
          }
        }

        result = result.filter((map) => map.seasonId === selectedSeason.id);

        const maps = groupBy(result, 'fieldId');
        for (const key of Object.keys(maps)) {
          const field = props.fields.get(Number(key));
          if (field) {
            cards.push(new PrescriptionFieldCardItem(field, maps[key], []));
          } else {
            mapsWithUnknownFields.push(...maps[key]);
          }
        }

        if (mapsWithUnknownFields.length > 0 || outsideMapsWithUnknownFields.length > 0) {
          const unknownField = new Field(
            'unknown-field-id',
            '',
            LangFile.PrescriptionArchive.unknownField
          );
          unknownField.crop = LangFile.CropType['NONE'];

          cards.push(
            new PrescriptionFieldCardItem(
              unknownField,
              mapsWithUnknownFields,
              outsideMapsWithUnknownFields
            )
          );
        }

        const displayedFieldIds = new Set();
        cards.forEach((card) => {
          [...card.currentMaps, ...card.outsideMaps].forEach((map) =>
            displayedFieldIds.add(map.fieldId)
          );
        });
        setExcludedFields(numberOfFields - displayedFieldIds.size);
        setFilteredAndSortedMaps(cards);
      }
    };

    filterMaps();
  }, [
    order,
    orderBy,
    props.maps,
    props.fields,
    selectedSeason,
    metaTypes,
    cropTypes,
    dataSources,
    showOutsideMaps,
    searchText,
    datePickerFromDate,
    datePickerToDate,
  ]);

  useEffect(() => {
    setSelectedJobs([]);
    filteredAndSortedMaps ? setCards(sortCards(filteredAndSortedMaps)) : [];
  }, [filteredAndSortedMaps]);

  return (
    <Box data-testid="prescription-archive">
      <Card elevation={4} className={Styles.root}>
        <AppBar elevation={0} position={'static'}>
          <Toolbar className={Styles.toolbar}>
            <Box className={Styles.buttonWrapper}>
              <IconButton className={Styles.menuButton} onClick={props.onClose}>
                <ArrowBackIcon />
              </IconButton>
              <SeasonSelector color={'white'} selectOnly={true} onChange={props.onSeasonChange} />
            </Box>
            <SearchBar
              className={Styles.search}
              value={searchText}
              onChange={onSearchChange}
              onCancelSearch={onSearchCancel}
            />
          </Toolbar>
          <Box className={Styles.filter}>
            <Box className={Styles.datePickerBox}>
              <FormControlLabel
                className={datePickerEnabled ? null : Styles.datePickerLabel}
                control={
                  <Checkbox
                    disableRipple
                    color="default"
                    classes={{ root: Styles.checkbox }}
                    checked={datePickerEnabled}
                    onChange={ondatePickerEnabledChange}
                  />
                }
                label={LangFile.PrescriptionArchive.dateFilter}
              />
              <DateRangeInput
                startDate={datePickerFromDate}
                endDate={datePickerToDate}
                onChanged={handleDateRangeChange}
                disabled={datePickerEnabled ? false : true}
              />
            </Box>
            <PrescriptionArchiveSelector
              title={LangFile.PrescriptionArchive.jobType}
              items={validMetaTypes.current.map(
                (metaType) =>
                  new PrescriptionArchiveSelectorItem(metaType, parseMetaType(metaType, LangFile))
              )}
              initialSelections={metaTypes.map(
                (metaType) =>
                  new PrescriptionArchiveSelectorItem(metaType, parseMetaType(metaType, LangFile))
              )}
              orderBy={TableColumns.metaType}
              selectedOrderBy={orderBy}
              onChange={onJobTypesChange}
              onOrderByChange={onOrderByChange}
              onOrderChange={onOrderChange}
            />
            <PrescriptionArchiveSelector
              title={LangFile.PrescriptionArchive.cropType}
              items={validCropTypes.current.map(
                (cropType) =>
                  new PrescriptionArchiveSelectorItem(cropType, LangFile.CropType[cropType])
              )}
              initialSelections={cropTypes.map(
                (cropType) =>
                  new PrescriptionArchiveSelectorItem(cropType, LangFile.CropType[cropType])
              )}
              orderBy={TableColumns.crop}
              selectedOrderBy={orderBy}
              onChange={onCropTypesChange}
              onOrderByChange={onOrderByChange}
              onOrderChange={onOrderChange}
            />
            <PrescriptionArchiveSelector
              title={LangFile.PrescriptionArchive.inputLayer}
              items={validDataSources.current.map(
                (surveyLayer) =>
                  new PrescriptionArchiveSelectorItem(
                    surveyLayer,
                    parseLayerName(surveyLayer, LangFile)
                  )
              )}
              initialSelections={dataSources.map(
                (surveyLayer) =>
                  new PrescriptionArchiveSelectorItem(
                    surveyLayer,
                    parseLayerName(surveyLayer, LangFile)
                  )
              )}
              orderBy={TableColumns.layer}
              selectedOrderBy={orderBy}
              onChange={onDataSourcesChange}
              onOrderByChange={onOrderByChange}
              onOrderChange={onOrderChange}
            />
            <FormControlLabel
              className={Styles.formLabel}
              control={
                <Checkbox
                  disableRipple
                  color="default"
                  classes={{ root: Styles.checkbox }}
                  checked={showOutsideMaps}
                  onChange={onShowMapsOutsideChange}
                />
              }
              label={LangFile.PrescriptionArchive.showMapsOutsideOfSeason}
            />
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                alignSelf: 'flex-end',
                paddingBottom: '12px',
                ml: 'auto',
              }}
            >
              <Box>
                <Typography variant="subtitle2" className={Styles.unClickableTypography}>
                  {selectedJobs.length > 0
                    ? LangFile.PrescriptionArchive.exportSelectedJobs
                    : LangFile.PrescriptionArchive.exportTableJobs}
                </Typography>
                {selectedJobs.length > 0 && (
                  <>
                    <Typography variant="subtitle2" className={Styles.unClickableTypography}>
                      {LangFile.PrescriptionArchive.selectedJobs + `: ${selectedJobs.length}`}
                    </Typography>
                    <Typography
                      color="primary"
                      variant="body2"
                      onClick={() => setSelectedJobs([])}
                      className={Styles.clickableTypography}
                    >
                      {LangFile.PrescriptionArchive.resetSelectedJobs}
                    </Typography>
                  </>
                )}
              </Box>
              <Tooltip
                title={
                  selectedJobs.length > 0
                    ? LangFile.PrescriptionArchive.exportSelectedJobsTooltip
                    : LangFile.PrescriptionArchive.exportTableJobsTooltip
                }
              >
                <IconButton onClick={generateCSVFromCards(cards)}>
                  <DescriptionIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </AppBar>
      </Card>
      <Box style={{ height: `4px` }} />
      <Box className={Styles.container}>
        <Box className={Styles.innerContainer}>
          {props.loadingMaps ? (
            <Box className={Styles.loadingWrapper}>
              <CircularProgress
                data-testid="prescription-archive-loading"
                variant={'indeterminate'}
                size={30}
                color="primary"
              />
            </Box>
          ) : (
            <PrescriptionArchiveTable
              cards={cards}
              documentation={props.documentation}
              numberOfExcludedFields={excludedFields}
              onFilterReset={setDefaultFilters}
              onDocumentationUploaded={props.onDocumentationUploaded}
              onDeleteDocumentation={props.onDeleteDocumentation}
              onSetSeason={props.onSetSeason}
              onDownload={props.onDownload}
              onDelete={props.onDelete}
              onEdit={props.onEdit}
              generateCSV={generateCSVFromJobs}
              onJobCheckboxToggled={onJobCheckboxToggled}
              selectedJobs={selectedJobs}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

PrescriptionArchive.propTypes = {
  maps: PropTypes.array,
  documentation: PropTypes.object,
  fields: PropTypes.object,
  loadingMaps: PropTypes.bool,
  selectedField: PropTypes.object,
  onClose: PropTypes.func,
  onSetSeason: PropTypes.func,
  onDocumentationUploaded: PropTypes.func,
  onDeleteDocumentation: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onDownload: PropTypes.func,
  onSeasonChange: PropTypes.func,
};

PrescriptionArchive.defaultProps = {
  maps: [],
  onClose: voidFunc,
  onSetSeason: voidFunc,
  onDocumentationUploaded: voidFunc,
  onDeleteDocumentation: voidFunc,
  onEdit: voidFunc,
  onDelete: voidFunc,
  onDownload: voidFunc,
  onSeasonChange: voidFunc,
};

export default memo(PrescriptionArchive);
